<template>
  <v-form ref="form" >

    <BaseUtil
      ref="util"
      :pageLoading="pageLoading"
    />

    <v-row>

      <v-col cols="12" v-if="!entry.isNewItemInStorage">
        <v-autocomplete
          v-model="entry.itemId"
          v-bind="getObject('itemId')"
          label="Edição do Item"
          :items="extraData.accountContext.availableItemsForStorage"
          item-value="id"
          item-text="name"
          @input="selectItem(true)"
          :hint="itemAdditionalInfo(entry.item)"
          persistent-hint
          autocomplete="off"
          :disabled="readOnly"
        >
          <template v-slot:item="data">
            <v-list-item-content>
              <v-list-item-title>{{data.item.name}}</v-list-item-title>
              <v-list-item-subtitle>{{itemAdditionalInfo(data.item)}}</v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>
      
      <v-col cols="12" v-if="entry.isNewItemInStorage">
        <v-autocomplete
          v-model="entry.itemId"
          v-bind="getObject('itemId')"
          label="Novo Item em Estoque"
          :items="itemsList"
          item-value="id"
          item-text="name"
          :search-input.sync="itemsSearch"
          no-filter
          @input="selectItem(true)"
          no-data-text="Nenhum Item encontrado, faça outra busca"
          :hint="itemAdditionalInfo(entry.item)"
          persistent-hint
          autocomplete="off"
          :disabled="readOnly"
        >
          <template v-slot:item="data">
            <v-list-item-content>
              <v-list-item-title>{{data.item.name}}</v-list-item-title>
              <v-list-item-subtitle>{{itemAdditionalInfo(data.item)}} </v-list-item-subtitle>
            </v-list-item-content>
          </template>

          <template v-slot:append-outer>
            <NewItemSelect
              ref="newItemSelect"
              newTitle="Adicionar Item"
              width="50%"
              openSelectButtonLabel="Adicionar Item"
              :inlineAddButton="true"
              @entry-created="itemCreated"
            />
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12">
        <v-autocomplete
          :disabled="!entry.itemId || readOnly"
          v-model="entry.packagingId"
          v-bind="getObject('packagingId')"
          label="Embalagem"
          :items="packagingList"
          item-value="id"
          item-text="name"
          @input="selectPackaging"
          :loading="packagingListLoading"
          autocomplete="off"
        >
          <!-- <template v-slot:append-outer>
            <PackagingNew
              :inlineAddButton="true"
              @entry-created="packagingCreated"
            />
          </template> -->
        </v-autocomplete>
      </v-col>

      <v-col cols="12" v-if="entry.isNewItemInStorage && entry.enabledLots">
        <v-switch 
          v-model="entry.hasLot"
          inset
          :label="entry.hasLot ? 'Informar lote' : 'Não informar lote'"
          :readonly="!storageAdjustment.isStockEntryType"
          :disabled="readOnly"
        >
        </v-switch>
      </v-col>

      <v-col cols="12" v-if="storageAdjustment.isStockEntryType && (!entry.isNewItemInStorage && hasLot || (entry.hasLot))">
        <StorageAdjustmentItemLotIndex
          :lotItems="entry.entryMovementItemLotsAttributes"
          :packaging="entry.packaging"
          :inputData.sync="entry.entryMovementItemLotsAttributes"
          :readOnly="readOnly"
          :itemLots="itemLots"
          :hasTableTitle="true"
          :movementItem="entry"
          :isNewItemInStorage="entry.isNewItemInStorage"
          :storageAdjustment="storageAdjustment"
        />
      </v-col>

      <v-col cols="12" v-if="!storageAdjustment.isStockEntryType && hasLot && storageAdjustment.removeFromStorage">
        <StorageAdjustmentItemLotIndex
          :lotItems="entry.exitMovementItemLotsAttributes"
          :packaging="entry.packaging"
          :inputData.sync="entry.exitMovementItemLotsAttributes"
          :itemLots="itemLots"
          :readOnly="readOnly"
          :hasTableTitle="true"
          :movementItem="entry"
          :storageAdjustment="storageAdjustment"
        />
        </v-col>

        <v-col cols="12" v-if="!storageAdjustment.isStockEntryType && hasLot && !storageAdjustment.removeFromStorage">
          <StorageAdjustmentItemLotIndex
            :lotItems="entry.dummyExitMovementItemLotsAttributes"
            :packaging="entry.packaging"
            :inputData.sync="entry.dummyExitMovementItemLotsAttributes"
            :itemLots="itemLots"
            :readOnly="readOnly"
            :hasTableTitle="true"
            :movementItem="entry"
            :storageAdjustment="storageAdjustment"
          />
        </v-col>

      <v-col cols="12" sm="6">
        <MaskedTextField
          v-if="renderComponent"
          :inputData.sync="packagingQuantity"
          mask-type="currency_10d"
          :label="'Qtd. de Emb. ' + (storageAdjustment.isStockEntryType ? 'Adicionadas' : 'Removidas')"
          :disabled="!entry.packagingId || readOnly || (!entry.isNewItemInStorage && hasLot) || (entry.hasLot)"
          @update:inputData="changePackagingQuantity" 
          :hint="entry.packagingId ? ('Qtd. de emb. disponível em estoque: ' + numberToDecimal(calculatePackagingInStorageQuantity) + ' ' + entry.packaging.text) : ''"
          :persistent-hint="entry.packagingId ? true : false"
          autocomplete="off"
          :suffix="entry.packaging ? entry.packaging.text : ''"
          :key="packagingQuantityKey + 'packagingQuantity'"
        />
        <!--   -->
      </v-col>

      <v-col cols="12" sm="6">
        <MaskedTextField
          v-if="renderComponent"
          v-bind="getObject('quantity')"
          :inputData.sync="entry.quantity"
          mask-type="currency_10d"
          :label="'Qtd. Unitária ' + (storageAdjustment.isStockEntryType ? 'Adicionada' : 'Removida')"
          :disabled="(!entry.packagingId || readOnly) || (!entry.isNewItemInStorage && hasLot) || (entry.hasLot)"
          @update:inputData="changeQuantity"
          :hint="entry.packagingId ? ('Qtd. disponível em estoque: ' + numberToDecimal(availableQuantity) + (entry.item ? entry.item.unit.name : '')) : ''"
          :persistent-hint="entry.packagingId ? true : false"
          autocomplete="off"
          :suffix="entry.item ? entry.item.unit.name : ''"
          :key="quantityKey + 'quantity'"
        />
        <!-- -->
      </v-col>

    </v-row>
  </v-form>
</template>

<script>
import MaskedTextField from "@shared_components/masked_text_field.vue"
import gql from 'graphql-tag'
import BaseUtil from "@shared_components/base_util.vue"
import NewItemSelect from "@models/purchase_items/new_item_select.vue"
import StorageAdjustmentItemLotIndex from "../storage_adjustment_items/storage_adjustment_item_lots/index.vue"

export default {
  data() {
    return {
      entryAttributes: {},
      itemsList: [],
      itemsListLoading: false,
      packagingListLoading: false,
      packagingList: [],
      renderComponent: true,
      itemsSearch: "",
      unitPriceKey: 0,
      totalPriceKey: 0,
      packagingQuantity: 0,
      packagingQuantityKey: 0,
      quantityKey: 0,
      packagingPriceKey: 0,
      availableQuantityLoading: false,
      availableQuantity: 0,
      itemLastPrice: 0,
      itemLastPriceInStorage: 0,
      pageLoading: false,
      hasLot: false,
      itemLots: [],
      timer: null,
    };
  },
  props: {
    entry: Object,
    extraData: Object,
    storageAdjustment: Object,
    isEdit: { // utilizado para definir se o form é de edição
      type: Boolean,
      default: false
    },
    isLoss: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    modelNameSingular: String,
  },
  components: {
    MaskedTextField: MaskedTextField,    
    BaseUtil: BaseUtil,
    NewItemSelect: NewItemSelect,
    StorageAdjustmentItemLotIndex: StorageAdjustmentItemLotIndex,
  },
  created() {
    this.entryAttributes = this.setupEntryAttributes(this.entry);
    // console.log("entry", this.entry);
    // console.log("hasLot", this.hasLot);
    if (this.entry.item) {
      if (this.entry.isNewItemInStorage) {
        this.itemsList = [this.entry.item];
        // let item = this.itemsList.find(obj => obj.id == this.entry.itemId);
        // if (!item) {
        //   this.itemsList.unshift(this.entry.item);
        // }
      } else {
        // console.log("extra", this.extraData.accountContext.availableItemsForStorage);
        let item = this.extraData.accountContext.availableItemsForStorage.find(obj => obj.id == this.entry.itemId);
        if (!item) {
          this.extraData.accountContext.availableItemsForStorage.unshift(this.entry.item);
        }
      }
      this.selectItem(false);
      this.changeQuantity();
    }
  },
  mounted() {
  },
  computed: {
    calculatePackagingInStorageQuantity: function() {
      return _.round(this.availableQuantity / this.entry.packaging.size, 10);
    },
  },
  watch: {
    itemsSearch: function(newSearch, oldSearch) {
      // console.log("newSearch", newSearch, "oldSearch", oldSearch)
      if (this.itemsSearch && this.itemsSearch.length > 0) {
        if ((!this.entry.item || this.entry.item.name != this.itemsSearch) && newSearch.length >= 3) {
          if(this.timer){
            clearTimeout(this.timer);
          }
          this.timer = setTimeout( ()=> {
            console.log("newSearch", newSearch);
            this.getItems();
          }, 2000);
        }
      }
    },
    "entry.exitMovementItemLotsAttributes": {
      handler: function(newValue, oldValue) {        
          // console.log("exit edited !!!");
          this.updateExitLots();
      },
      deep: true,
    },
    "entry.entryMovementItemLotsAttributes": {
      handler: function(newValue, oldValue) {        
          console.log("entry edited !!!");
          this.updateEntryLots();
      },
      deep: true,
    },
    "entry.dummyExitMovementItemLotsAttributes": {
      handler: function(newValue, oldValue) {        
          console.log("entry edited !!!");
          this.updateDummyExitLots();
      },
      deep: true,
    },
  },
  methods: {
    itemAdditionalInfo: function(item) {
      let info = ""
      if (item) {
        info = 'Unidade: ' + (item.unit ? item.unit.name : '') + (item.brand ? ', Marca: ' + item.brand.name : '') + (item.typeText ? ', Tipo: ' + item.typeText : '')
      }
      return info;
    },
    changeQuantity: function() {
      this.packagingQuantity = _.round(this.entry.quantity / this.entry.packaging.size, 10);
      this.packagingQuantityKey++;
      this.updateQuantity();
    },
    updateExitLots: function(){      
      // console.log("addExitLots");
      this.packagingQuantity = this.entry.exitMovementItemLotsAttributes.reduce((accumulator, object)=>{
        // console.log("packaging quantity:", object.packagingQuantity);
        return (accumulator) + (object.packagingQuantity);
      }, 0);
      this.packagingQuantityKey++;
      this.changePackagingQuantity();    
    },
    updateEntryLots: function(){      
      // console.log("addEntryLots");
      this.packagingQuantity = this.entry.entryMovementItemLotsAttributes.reduce((accumulator, object)=>{
        // console.log("packaging quantity:", object.packagingQuantity);
        return (accumulator) + (object.packagingQuantity);
      }, 0);
      this.packagingQuantityKey++;
      this.changePackagingQuantity();    
    },
    updateDummyExitLots: function(){      
      // console.log("addEntryLots");
      this.packagingQuantity = this.entry.dummyExitMovementItemLotsAttributes.reduce((accumulator, object)=>{
        // console.log("packaging quantity:", object.packagingQuantity);
        return (accumulator) + (object.packagingQuantity);
      }, 0);
      this.packagingQuantityKey++;
      this.changePackagingQuantity();
    },
    updateQuantity: function() {
      if (this.entry.unitPrice > 0) {
        this.calculatePackagingPrice();
        this.calculateTotalPrice();
      } else if (this.entry.price > 0) {
        this.calculateUnitPrice();
        this.calculatePackagingPrice();
      } else {
        this.entry.price = 0;
        this.totalPriceKey++;
        this.entry.unitPrice = 0;
        this.unitPriceKey++;
        this.entry.packagingPrice = 0;
        this.packagingPriceKey++;
      }
    },
    changePackagingQuantity: function() {
      this.entry.quantity = _.round(this.packagingQuantity * this.entry.packaging.size, 10);
      this.quantityKey++;
      this.updateQuantity();
    },
    changeUnitPrice: function() {
      if (this.entry.quantity > 0) {
        this.calculatePackagingPrice();
        this.calculateTotalPrice();
      }
    },
    changePackagingPrice: function() {
      if (this.entry.quantity > 0) {
        this.calculateUnitPrice(false);
        this.calculateTotalPrice();
      }
    },
    changeTotalPrice: function() {
      if (this.entry.quantity > 0) {
        this.calculateUnitPrice();
        this.calculatePackagingPrice();
      }
    },
    calculateTotalPrice: function() {
      this.entry.price = _.round((this.entry.unitPrice ? this.entry.unitPrice : 0) * (this.entry.quantity ? this.entry.quantity : 0), 2);
      this.totalPriceKey++;
    },
    calculateUnitPrice: function(byTotalPrice=true) {
      if (byTotalPrice) {
        this.entry.unitPrice = _.round((this.entry.price ? this.entry.price : 0) / (this.entry.quantity ? this.entry.quantity : 0), 10);
      } else { //calcula pelo preco unitario com base no preco da embalagem
        this.entry.unitPrice = _.round((this.entry.packagingPrice ? this.entry.packagingPrice : 0) / (this.entry.packaging.size ? this.entry.packaging.size : 0), 10);
      }
      this.unitPriceKey++;
    },
    calculatePackagingPrice: function() {
      this.entry.packagingPrice = _.round((this.entry.unitPrice ? this.entry.unitPrice : 0) * (this.entry.packaging.size ? this.entry.packaging.size : 0), 10);
      this.packagingPriceKey++;
    },
    getObject: function(element, component) {
      return this.entryAttributes[element];
    },
    isFormValid: function() {
      if (this.$refs.form.validate()) {
        return true;
      } else {
        setTimeout(function(){
          document.querySelector(".v-messages.error--text:first-of-type").scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 500);
      }
      return false;
    },
    resetFormInputs: function() {
      this.$refs.form.resetValidation();
    },
    isNewItemInStorageChanged: function() {
      // if (this.entry.isNewItemInStorage) {
        this.entry.itemId = null;
        this.itemsList = [];
        this.itemsSearch = "";
        this.entry.packagingId = null;
        this.entry.packaging = null;
        this.packagingList = [];
        this.entry.quantity = null;
        this.packagingQuantity = 0;
        this.entry.unitPrice = null;
        this.entry.packagingPrice = null;
        this.entry.price = null;
        this.unitPriceKey++;
        this.totalPriceKey++;
        this.packagingQuantityKey++;
        this.quantityKey++;
        this.packagingPriceKey++;
      // } 
    },
    getItems: function() {
      this.itemsListLoading = true;
      this.$apollo
        .query({
          query: ITEMS_SEARCH_QUERY,
          variables: {
            query: this.itemsSearch
          },
          fetchPolicy: "no-cache"
        })
        .then(response => {
          this.itemsList = response.data.accountGlobalSharedContext.allAvailableItemsForPurchase;
          // if (this.packageList.length == 1 && !this.entry.packageId) {
          //   this.entry.packageId = this.packageList[0].id;
          //   this.selectPackage();
          // }
        })
        .catch(response => console.log(response))
        .finally(() => (this.itemsListLoading = false));
    },
    selectItem: function(clearPackaging, newPackaging=null) {      
      // console.log("item, itemID", this.entry.item, this.entry.itemId)

      // this.clearFields();
      if (clearPackaging) {
        this.entry.packagingId = null;
        this.entry.packaging = null;
        this.packagingList = [];
        while(this.entry.exitMovementItemLotsAttributes?.length>0){
          this.entry.exitMovementItemLotsAttributes.pop();
        }
        while(this.entry.entryMovementItemLotsAttributes?.length>0){
          this.entry.entryMovementItemLotsAttributes.pop();
        }
      }
      this.packagingListLoading = true;
      
      if (this.entry.isNewItemInStorage) {
        this.entry.item = this.itemsList.find(obj => obj.id == this.entry.itemId);

        this.$apollo
          .query({
            query: PACKAGINGS_FOR_ITEM_QUERY,
            fetchPolicy: "no-cache",
            variables: {
              itemId: this.entry.itemId
            }
          })
          .then(response => {
            this.packagingList = response.data.accountGlobalSharedContext.allPackagingsOfItem;

            // console.log("this.packagingList",this.packagingList);

            if (newPackaging) {
              let result_find = this.packagingList.find(obj => obj.id == newPackaging.id);
              if (result_find) {
                this.entry.packagingId = newPackaging.id;
                this.entry.packaging = newPackaging;
                // this.selectPackaging();
              } else {
                this.$refs.util.showErrorMessage("A embalagem criada não é compatível com o item selecionado, por isso não irá aparecer na lista de embalagens!");
              }
            } else if (this.packagingList.length == 1 && !this.entry.packagingId) {
              this.entry.packagingId = this.packagingList[0].id;
              this.entry.packaging = this.packagingList.find(obj => obj.id == this.entry.packagingId);
              // this.selectPackage();
            }
          })
          .catch(response => console.log(response))
          .finally(() => (this.packagingListLoading = false));
      } else {
        this.entry.item = this.extraData.accountContext.availableItemsForStorage.find(obj => obj.id == this.entry.itemId);

        this.$apollo
          .query({
            query: AVAILABLE_PACKAGING_FOR_ITEM_QUERY,
            fetchPolicy: "no-cache",
            variables: {
              itemId: this.entry.itemId,
              storageId: this.storageAdjustment.storageId,
              packagingId: this.entry.packagingId || 0
            }
          })
          .then(response => {
            // console.log("response dos packaging", response);
            this.packagingList = response.data.accountContext.availableItemPackagingsForStorage;

            // if (this.entry.item && this.isEdit) {
            //   let packagingFromList = this.packagingList.find(obj => obj.id == this.entry.packagingId)
            //   if (!packagingFromList) {
            //     this.packagingList.unshift(this.entry.packaging)
            //   }
            // }

            if (this.packagingList.length == 1 && !this.entry.packagingId) {
              this.entry.packagingId = this.packagingList[0].id;
            }
            this.selectPackaging();
          })
          .catch(response => console.log(response))
          .finally(() => (this.packagingListLoading = false));
      }
    },
    availableLots: function(){
      this.$apollo
          .query({
            query: AVAILABLE_LOTS_FOR_ITEM_QUERY,
            fetchPolicy: "no-cache",
            variables: {
              itemId: this.entry.itemId,
              packagingId: this.entry.packagingId,
              storageId: this.storageAdjustment.storageId,
              movementItemLotId: this.entry.id,
            }
          })
          .then(response => {
            // console.log("response dos storages", response)
            this.itemLots = response.data.accountContext.availableLots;
            console.log("lots", this.itemLots);
            if(this.itemLots.length > 0){
              this.hasLot = this.itemLots[0].hasLot;
            }else{
              this.hasLot = false;
            }
            console.log("available_lots", this.itemLots);
          })
          .catch(response => console.log(response))
          .finally(() => (this.availableQuantityLoading = false));
    },
    selectPackaging: function() {
      this.entry.packaging = this.packagingList.find(obj => obj.id == this.entry.packagingId);

      if (!this.entry.isNewItemInStorage) {
        this.availableQuantityLoading = true;
        this.$apollo
          .query({
            query: AVAILABLE_QUANTITY_AND_PRICE_FOR_ITEM_QUERY,
            fetchPolicy: "no-cache",
            variables: {
              itemId: this.entry.itemId,
              packagingId: this.entry.packagingId,
              storageId: this.storageAdjustment.storageId
            }
          })
          .then(response => {
            // console.log("response dos storages", response)
            this.availableQuantity = response.data.accountContext.availableQuantityForItem;
            this.itemLastPrice = response.data.accountContext.lastPriceForItem;
            this.itemLastPriceInStorage = response.data.accountContext.lastPriceForItemInStorage;

            // console.log(this.availableQuantity, this.itemLastPrice, this.itemLastPriceInStorage)

            if (this.itemLastPriceInStorage > 0 ) {
              this.entry.unitPrice = this.itemLastPriceInStorage;
              this.unitPriceKey++;
              this.changeUnitPrice();
            } else if (this.itemLastPrice > 0 ) {
              this.entry.unitPrice = this.itemLastPrice;
              this.unitPriceKey++;
              this.changeUnitPrice();
            }

            if (!this.storageAdjustment.isStockEntryType) {
              this.entryAttributes.quantity.rules = [];
              this.entryAttributes.quantity.rules.push(this.requiredRule);
              this.entryAttributes.quantity.rules.push(this.minRequiredRule);
              let availableQuantity = _.round(this.availableQuantity, 10);
              //TODO ver que ainda nao esta funcionando direito!!!!
              // usado para contabilizar a quantidade já salva no edit 
              // console.log("OrigEntry", this.originalEntry);
              // if (this.isEdit && this.originalEntry?.id) {
              //   availableQuantity = _.round(this.availableQuantity + this.entry.quantity, 10);
              // }
              this.entryAttributes.quantity.rules.push(this.maxValueRule(availableQuantity));
            }
          })
          .catch(response => console.log(response))
          .finally(() => (this.availableQuantityLoading = false));
      } else {
        this.changePackagingQuantity();
      }

      // this.availableLots();

      // return;
      // this.entryAttributes.quantity.rules = [];
      // this.entryAttributes.quantity.rules.push(this.requiredRule);
      // this.entryAttributes.quantity.rules.push(this.minRequiredRule);
      // // this.entryAttributes.quantity.rules.push(this.maxValueRule(this.entry.storehouse.availableQuantity));

      // this.entryAttributes.price.rules = [];
      // this.entryAttributes.price.rules.push(this.requiredRule);
      // this.entryAttributes.price.rules.push(this.minRequiredRule);
      // this.clearFields();
    },
    clearFields: function() {
      this.forceRerender(this);
    },    
    packagingCreated: function(packaging) {
      this.selectItem(true, packaging);
    },
    itemCreated: function(item) {
      // console.log("item created", item)
      this.itemsList.unshift(item);
      this.entry.itemId = item.id;
      this.entry.item = item;
      this.selectItem(true);
    },
  }
};

const ITEMS_SEARCH_QUERY = gql `
  query ($query: String!) {
    accountGlobalSharedContext {
      allAvailableItemsForPurchase(query: $query) {
        id
        name
        unit {
          name
        }
        brand {
          name
        }
        typeText
      }
    }
  }
`;

const PACKAGINGS_FOR_ITEM_QUERY = gql `
  query ($itemId: ID!) {
    accountGlobalSharedContext {
      allPackagingsOfItem(itemId: $itemId) {
        id
        name
        unitName
        size
        text
      }
    }
  }
`;

const AVAILABLE_PACKAGING_FOR_ITEM_QUERY = gql `
  query ($itemId: ID!, $storageId: ID!, $packagingId: ID!) {
    accountContext {
      availableItemPackagingsForStorage(itemId: $itemId, storageId: $storageId, packagingId: $packagingId){
        id
        name
        unitName
        text
        size
      }
    }
  }
`;

const AVAILABLE_QUANTITY_AND_PRICE_FOR_ITEM_QUERY = gql `
  query ($itemId: ID!, $packagingId: ID!, $storageId: ID!) {
    accountContext {
      availableQuantityForItem(itemId: $itemId, packagingId: $packagingId, storageId: $storageId)
      lastPriceForItem(itemId: $itemId, packagingId: $packagingId)
      lastPriceForItemInStorage(itemId: $itemId, packagingId: $packagingId, storageId: $storageId)
    }
  }
`;

const AVAILABLE_LOTS_FOR_ITEM_QUERY = gql `
  query ($itemId: ID!, $packagingId: ID!, $storageId: ID!, $movementItemLotId: ID) {
    accountContext {
      availableLots(itemId: $itemId, packagingId: $packagingId, storageId: $storageId, movementItemLotId: $movementItemLotId)
    }
  }
`;

</script>

<style scoped></style>
